import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoneyBillWave, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useModalDismiss } from '../../../hooks/useModalDismiss';
import { RELANCE_MODES, DEFAULT_RELANCE_MODE, savePaymentRelance } from './PaymentRelanceCard';

/**
 * Demande le délai de paiement au moment où l'élément arrive en attente de paiement
 * (case « Facturation » cochée, ou carte déposée dans la colonne du closing).
 *
 * Fermer sans choisir laisse le défaut de 60 jours s'appliquer.
 */
const PaymentRelancePrompt = ({ isOpen, onClose, projectId, slotId = null, itemName, currentMode, onSaved }) => {
  const [mode, setMode] = useState(currentMode || DEFAULT_RELANCE_MODE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setMode(currentMode || DEFAULT_RELANCE_MODE);
      setError(null);
    }
  }, [isOpen, currentMode]);

  // Choix explicite : un clic à côté ne doit pas le fermer par accident.
  const dismiss = useModalDismiss(onClose, { backdropClose: false, enabled: isOpen });

  if (!isOpen) return null;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await savePaymentRelance(projectId, slotId, mode);
      if (onSaved) onSaved(res.data.data || null);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay} {...dismiss}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <FontAwesomeIcon icon={faMoneyBillWave} style={{ color: '#1d4ed8' }} />
          <span style={{ fontSize: '0.95rem', fontWeight: 700, color: '#242424' }}>Délai de paiement</span>
        </div>

        <div style={{ fontSize: '0.8rem', color: '#4b5563', marginBottom: 4 }}>
          {itemName ? <strong>{itemName}</strong> : null}
        </div>
        <div style={{ fontSize: '0.8rem', color: '#4b5563', marginBottom: 14 }}>
          Quel délai de règlement est accordé au client ? La relance partira à l'échéance, puis la
          direction sera alertée 7 jours plus tard si ce n'est pas encaissé.
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
          {RELANCE_MODES.map(m => {
            const active = mode === m.value;
            return (
              <button
                key={m.value}
                onClick={() => setMode(m.value)}
                style={{
                  textAlign: 'left', padding: '9px 12px', borderRadius: 8, cursor: 'pointer',
                  border: active ? '1.5px solid #1d4ed8' : '1px solid #e5e7eb',
                  background: active ? '#eff6ff' : '#fff',
                  color: active ? '#1e40af' : '#374151',
                  fontSize: '0.8rem', fontWeight: active ? 600 : 500,
                }}
              >
                {m.label}
                {m.value === DEFAULT_RELANCE_MODE && (
                  <span style={{ color: '#9ca3af', fontWeight: 400 }}> — par défaut</span>
                )}
                {m.value === 'reception' && (
                  <span style={{ color: '#9ca3af', fontWeight: 400, fontSize: '0.72rem' }}> — relance 10 j après la réception, puis 5 j plus tard</span>
                )}
              </button>
            );
          })}
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: '0.72rem', padding: '7px 9px', borderRadius: 6, marginBottom: 10 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} disabled={saving}
            style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#6b7280', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>
            Plus tard
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: '#1d4ed8', color: '#fff', fontSize: '0.78rem', fontWeight: 600, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? <FontAwesomeIcon icon={faSpinner} size="sm" spin /> : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
};

const overlay = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center',
  justifyContent: 'center', zIndex: 1200, padding: 16,
};

const modal = {
  background: '#fff', borderRadius: 12, padding: '20px 22px',
  width: '100%', maxWidth: 420, boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
};

export default PaymentRelancePrompt;
